import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

// ─── Questionnaire Definition ────────────────────────────────────────────────
const SECTIONS = [
  {
    key: 'access_controls',
    title: 'Access Controls',
    questions: [
      { id: 'AC-01', text: 'Are unique user IDs assigned to every workforce member?' },
      { id: 'AC-02', text: 'Is multi-factor authentication enabled for all system access?' },
      { id: 'AC-03', text: 'Are automatic session timeouts configured for inactive users?' },
      { id: 'AC-04', text: 'Are strong password policies enforced (min length, complexity, expiration)?' },
      { id: 'AC-05', text: 'Is least-privilege access implemented for all roles?' },
    ],
  },
  {
    key: 'workforce_security',
    title: 'Workforce Security',
    questions: [
      { id: 'WS-01', text: 'Are background checks performed for employees with ePHI access?' },
      { id: 'WS-02', text: 'Are termination procedures in place to revoke access immediately?' },
      { id: 'WS-03', text: 'Is security awareness training provided to all staff annually?' },
      { id: 'WS-04', text: 'Are access levels reviewed and updated regularly?' },
    ],
  },
  {
    key: 'data_protection',
    title: 'Data Protection',
    questions: [
      { id: 'DP-01', text: 'Is all ePHI encrypted at rest using AES-256 or equivalent?' },
      { id: 'DP-02', text: 'Is all ePHI encrypted in transit using TLS 1.2+?' },
      { id: 'DP-03', text: 'Are regular data backup procedures documented and tested?' },
      { id: 'DP-04', text: 'Are secure data disposal procedures in place for hardware and media?' },
      { id: 'DP-05', text: 'Are encryption keys managed with proper rotation and access controls?' },
    ],
  },
  {
    key: 'physical_security',
    title: 'Physical Security',
    questions: [
      { id: 'PS-01', text: 'Are facility access controls in place (badges, locks, cameras)?' },
      { id: 'PS-02', text: 'Are workstation use and security policies documented?' },
      { id: 'PS-03', text: 'Is a complete device and hardware inventory maintained?' },
      { id: 'PS-04', text: 'Are visitor access logs maintained for areas with ePHI access?' },
    ],
  },
  {
    key: 'contingency_planning',
    title: 'Contingency Planning',
    questions: [
      { id: 'CP-01', text: 'Is a disaster recovery plan documented and tested?' },
      { id: 'CP-02', text: 'Are data backup and recovery procedures documented?' },
      { id: 'CP-03', text: 'Is the emergency mode operation plan tested regularly?' },
      { id: 'CP-04', text: 'Is a business impact analysis performed at least annually?' },
    ],
  },
  {
    key: 'device_media_controls',
    title: 'Device & Media Controls',
    questions: [
      { id: 'DM-01', text: 'Is a hardware and electronic media inventory maintained?' },
      { id: 'DM-02', text: 'Are secure media disposal procedures documented?' },
      { id: 'DM-03', text: 'Are media reuse procedures documented to ensure ePHI removal?' },
      { id: 'DM-04', text: 'Is hardware movement tracked and authorized?' },
    ],
  },
];

const TOTAL_QUESTIONS = SECTIONS.reduce((sum, s) => sum + s.questions.length, 0);

// ─── Remediation hints keyed by question ID ──────────────────────────────────
const REMEDIATION_HINTS = {
  'AC-01': 'Implement a user provisioning system that assigns unique identifiers to every workforce member upon onboarding.',
  'AC-02': 'Deploy a multi-factor authentication solution (TOTP, hardware key, or push-based) for all systems containing ePHI.',
  'AC-03': 'Configure automatic session lock/logout after 15 minutes of inactivity across all workstations and applications.',
  'AC-04': 'Establish a password policy requiring minimum 12 characters, mixed case, numbers, special characters, and 90-day expiration.',
  'AC-05': 'Conduct a role-based access review and restrict permissions to the minimum necessary for each job function.',
  'WS-01': 'Implement mandatory background checks for all new hires and contractors who will access ePHI.',
  'WS-02': 'Create a documented termination checklist that includes immediate revocation of all system and physical access.',
  'WS-03': 'Schedule annual HIPAA security awareness training for all staff and maintain completion records.',
  'WS-04': 'Perform quarterly access reviews to ensure workforce members only retain access appropriate to their current role.',
  'DP-01': 'Enable AES-256 encryption for all databases, file stores, and backups containing ePHI.',
  'DP-02': 'Enforce TLS 1.2 or higher for all network communications and disable older protocols.',
  'DP-03': 'Document backup procedures, test restores quarterly, and maintain off-site or cloud backup copies.',
  'DP-04': 'Implement a media sanitization policy using NIST SP 800-88 guidelines for disposal and recycling.',
  'DP-05': 'Use a key management system with automated rotation, access logging, and separation of duties for key custodians.',
  'PS-01': 'Install badge readers, deadbolt locks, and security cameras at all entrances to areas housing ePHI systems.',
  'PS-02': 'Create and distribute workstation use policies covering physical placement, screen privacy, and clean-desk rules.',
  'PS-03': 'Maintain an up-to-date inventory of all computing devices with serial numbers, locations, and assigned users.',
  'PS-04': 'Implement a visitor sign-in/sign-out log and escort policy for restricted areas.',
  'CP-01': 'Develop a disaster recovery plan covering RTOs and RPOs, and conduct a full DR test at least annually.',
  'CP-02': 'Document step-by-step backup and recovery procedures and verify them through quarterly restore tests.',
  'CP-03': 'Test emergency mode operations (degraded functionality) at least semi-annually with documented results.',
  'CP-04': 'Conduct an annual business impact analysis identifying critical systems, dependencies, and acceptable downtime.',
  'DM-01': 'Create a centralized inventory of all hardware and electronic media that store or process ePHI.',
  'DM-02': 'Document secure disposal procedures (degaussing, shredding, certified destruction) for all media types.',
  'DM-03': 'Establish media reuse procedures including verified data wiping and re-certification before reassignment.',
  'DM-04': 'Implement a hardware movement tracking system requiring authorization before relocating any ePHI-containing device.',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function safeParse(jsonStr, fallback = {}) {
  if (!jsonStr) return fallback;
  if (typeof jsonStr === 'object') return jsonStr;
  try { return JSON.parse(jsonStr); } catch { return fallback; }
}

function scoreColor(score) {
  if (score >= 80) return '#22c55e';
  if (score >= 60) return '#eab308';
  return '#ef4444';
}

function scoreLabel(score) {
  if (score >= 80) return 'Low Risk';
  if (score >= 60) return 'Moderate Risk';
  return 'High Risk';
}

function scoreBadgeClass(score) {
  if (score >= 80) return 'badge success';
  if (score >= 60) return 'badge warning';
  return 'badge danger';
}

// ─── Circular Score SVG ──────────────────────────────────────────────────────
function CircularScore({ score, size = 140, strokeWidth = 10 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.max(0, Math.min(100, score || 0));
  const offset = circumference - (pct / 100) * circumference;
  const color = scoreColor(pct);

  return (
    <svg width={size} height={size} style={{ display: 'block' }}>
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
      <text
        x={size / 2} y={size / 2 - 6}
        textAnchor="middle" dominantBaseline="central"
        style={{ fontSize: size * 0.25, fontWeight: 700, fill: color }}
      >
        {pct}
      </text>
      <text
        x={size / 2} y={size / 2 + 18}
        textAnchor="middle" dominantBaseline="central"
        style={{ fontSize: 11, fill: '#6b7280' }}
      >
        / 100
      </text>
    </svg>
  );
}

// ─── Score Bar ───────────────────────────────────────────────────────────────
function ScoreBar({ value, max = 100 }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div style={styles.scoreBarTrack}>
      <div style={{ ...styles.scoreBarFill, width: `${pct}%`, backgroundColor: scoreColor(value) }} />
    </div>
  );
}

// ─── Compute scores from responses ───────────────────────────────────────────
function computeScores(responses) {
  const sectionScores = {};
  let totalYes = 0;
  let totalQuestions = 0;

  for (const section of SECTIONS) {
    let yes = 0;
    for (const q of section.questions) {
      totalQuestions++;
      if (responses[q.id] === 'yes') {
        yes++;
        totalYes++;
      }
    }
    sectionScores[section.key] = section.questions.length > 0
      ? Math.round((yes / section.questions.length) * 100)
      : 0;
  }

  const overall = totalQuestions > 0 ? Math.round((totalYes / totalQuestions) * 100) : 0;
  return { overall, sectionScores };
}

// ─── Identify gaps (questions answered "no") ─────────────────────────────────
function identifyGaps(responses) {
  const gaps = [];
  for (const section of SECTIONS) {
    for (const q of section.questions) {
      if (responses[q.id] === 'no') {
        gaps.push({
          id: q.id,
          section: section.title,
          question: q.text,
          remediation: REMEDIATION_HINTS[q.id] || 'Review this control and implement appropriate safeguards.',
        });
      }
    }
  }
  return gaps;
}

// =============================================================================
// COMPLETED RESULTS VIEW
// =============================================================================
function CompletedResultsView({ assessment, responses, navigate }) {
  const { overall, sectionScores } = computeScores(responses);
  const gaps = identifyGaps(responses);
  const displayScore = assessment.inherent_risk_score != null ? assessment.inherent_risk_score : overall;

  return (
    <div>
      <div style={styles.resultsHeader}>
        <h2 style={styles.resultsTitle}>Risk Assessment Results</h2>
        <span className={scoreBadgeClass(displayScore)} style={{ fontSize: 14 }}>
          {scoreLabel(displayScore)}
        </span>
      </div>

      {/* Overall Score */}
      <div className="card" style={styles.scoreCard}>
        <div style={styles.scoreCardInner}>
          <CircularScore score={displayScore} />
          <div style={styles.scoreCardInfo}>
            <h3 style={{ margin: 0, fontSize: 18, color: '#111827' }}>Overall Risk Score</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              {TOTAL_QUESTIONS - gaps.length} of {TOTAL_QUESTIONS} controls satisfied
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>
              Completed: {assessment.completed_at
                ? new Date(assessment.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Section Breakdown */}
      <h3 style={styles.sectionHeading}>Section Breakdown</h3>
      <div style={styles.sectionGrid}>
        {SECTIONS.map(section => {
          const sScore = sectionScores[section.key] || 0;
          return (
            <div className="card" key={section.key} style={styles.sectionBreakdownCard}>
              <div style={styles.sectionBreakdownHeader}>
                <span style={styles.sectionBreakdownName}>{section.title}</span>
                <span style={{ fontSize: 20, fontWeight: 700, color: scoreColor(sScore) }}>{sScore}%</span>
              </div>
              <ScoreBar value={sScore} />
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
                {section.questions.filter(q => responses[q.id] === 'yes').length} / {section.questions.length} controls met
              </div>
            </div>
          );
        })}
      </div>

      {/* Identified Gaps */}
      {gaps.length > 0 && (
        <>
          <h3 style={styles.sectionHeading}>
            Identified Gaps
            <span className="badge danger" style={{ marginLeft: 8, fontSize: 12 }}>{gaps.length}</span>
          </h3>
          <div className="card" style={{ overflow: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Section</th>
                  <th>Control</th>
                  <th>Recommended Remediation</th>
                </tr>
              </thead>
              <tbody>
                {gaps.map(gap => (
                  <tr key={gap.id}>
                    <td><span className="badge">{gap.id}</span></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{gap.section}</td>
                    <td>{gap.question}</td>
                    <td style={{ fontSize: 12, color: '#374151' }}>{gap.remediation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {gaps.length === 0 && (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: '#22c55e', fontWeight: 600 }}>
          No gaps identified. All controls are satisfied.
        </div>
      )}

      {/* Back Button */}
      <div style={{ marginTop: 24 }}>
        <button className="button secondary" onClick={() => navigate('/practice-view/compliance')}>
          Back to Compliance
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// WIZARD STEP VIEW
// =============================================================================
function WizardStepView({ section, stepIndex, totalSteps, responses, onAnswer }) {
  return (
    <div>
      {/* Section Title */}
      <div style={styles.stepHeader}>
        <h3 style={styles.stepTitle}>{section.title}</h3>
        <span style={styles.stepCounter}>Section {stepIndex + 1} of {totalSteps}</span>
      </div>

      {/* Questions */}
      <div style={styles.questionList}>
        {section.questions.map((q) => {
          const answer = responses[q.id] || null;
          return (
            <div className="card" key={q.id} style={styles.questionCard}>
              <div style={styles.questionRow}>
                <span className="badge" style={styles.questionIdBadge}>{q.id}</span>
                <span style={styles.questionText}>{q.text}</span>
              </div>
              <div style={styles.radioGroup}>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name={q.id}
                    value="yes"
                    checked={answer === 'yes'}
                    onChange={() => onAnswer(q.id, 'yes')}
                    style={styles.radioInput}
                  />
                  <span style={{
                    ...styles.radioButton,
                    ...(answer === 'yes' ? styles.radioButtonYesActive : {}),
                  }}>
                    Yes
                  </span>
                </label>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name={q.id}
                    value="no"
                    checked={answer === 'no'}
                    onChange={() => onAnswer(q.id, 'no')}
                    style={styles.radioInput}
                  />
                  <span style={{
                    ...styles.radioButton,
                    ...(answer === 'no' ? styles.radioButtonNoActive : {}),
                  }}>
                    No
                  </span>
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
function RiskAssessmentWizard({ practiceId }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [assessment, setAssessment] = useState(null);
  const [responses, setResponses] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState(null);

  // ── Load assessment on mount ───────────────────────────────────────────────
  const loadAssessment = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getRiskAssessments(practiceId);
      const list = res.assessments || [];
      const match = list.find(a =>
        String(a.id) === String(id) || String(a.guid) === String(id)
      );
      if (!match) {
        setError('Risk assessment not found.');
        return;
      }
      setAssessment(match);

      // Pre-populate responses from saved data
      if (match.questionnaire_responses) {
        const parsed = safeParse(match.questionnaire_responses, {});
        setResponses(parsed);
      }
    } catch (err) {
      console.error('Error loading risk assessment:', err);
      setError(err.message || 'Failed to load risk assessment.');
    } finally {
      setLoading(false);
    }
  }, [practiceId, id]);

  useEffect(() => { loadAssessment(); }, [loadAssessment]);

  // ── Count answered questions ───────────────────────────────────────────────
  const questionsAnswered = Object.keys(responses).length;

  // ── Answer handler ─────────────────────────────────────────────────────────
  const handleAnswer = (questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  // ── Auto-save progress ─────────────────────────────────────────────────────
  const saveProgress = useCallback(async () => {
    if (!assessment || assessment.status === 'COMPLETED') return;
    try {
      setSaving(true);
      await api.updateRiskAssessmentProgress(practiceId, assessment.id || assessment.guid, {
        questionnaire_responses: JSON.stringify(responses),
        questions_answered: questionsAnswered,
        total_questions: TOTAL_QUESTIONS,
      });
    } catch (err) {
      console.error('Error saving progress:', err);
    } finally {
      setSaving(false);
    }
  }, [practiceId, assessment, responses, questionsAnswered]);

  // ── Navigation ─────────────────────────────────────────────────────────────
  const handlePrevious = async () => {
    if (currentStep > 0) {
      await saveProgress();
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleNext = async () => {
    if (currentStep < SECTIONS.length - 1) {
      await saveProgress();
      setCurrentStep(prev => prev + 1);
    }
  };

  // ── Complete assessment ────────────────────────────────────────────────────
  const handleComplete = async () => {
    try {
      setCompleting(true);
      await api.completeRiskAssessment(practiceId, assessment.id || assessment.guid, {
        questionnaire_responses: JSON.stringify(responses),
      });
      navigate('/practice-view/compliance');
    } catch (err) {
      console.error('Error completing assessment:', err);
      alert('Failed to complete assessment: ' + (err.message || 'Unknown error'));
    } finally {
      setCompleting(false);
    }
  };

  // ── Loading / Error states ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingMsg}>Loading risk assessment...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.errorMsg}>{error}</div>
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button className="button secondary" onClick={() => navigate('/practice-view/compliance')}>
            Back to Compliance
          </button>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div style={styles.page}>
        <div style={styles.emptyMsg}>Assessment not found.</div>
      </div>
    );
  }

  // ── Completed view ─────────────────────────────────────────────────────────
  if (assessment.status === 'COMPLETED') {
    return (
      <div style={styles.page}>
        <CompletedResultsView
          assessment={assessment}
          responses={responses}
          navigate={navigate}
        />
      </div>
    );
  }

  // ── Wizard view ────────────────────────────────────────────────────────────
  const currentSection = SECTIONS[currentStep];
  const isLastStep = currentStep === SECTIONS.length - 1;
  const progressPct = ((currentStep + 1) / SECTIONS.length) * 100;

  return (
    <div style={styles.page}>
      {/* Page Header */}
      <div style={styles.pageHeader}>
        <div style={styles.pageHeaderLeft}>
          <button
            className="button secondary"
            style={styles.backButton}
            onClick={() => navigate('/practice-view/compliance')}
          >
            Back
          </button>
          <h1 style={styles.pageTitle}>Risk Assessment Wizard</h1>
        </div>
        {saving && <span style={styles.savingIndicator}>Saving...</span>}
      </div>

      {/* Progress Bar */}
      <div className="card" style={styles.progressCard}>
        <div style={styles.progressInfo}>
          <span style={styles.progressLabel}>
            Step {currentStep + 1} of {SECTIONS.length}
          </span>
          <span style={styles.progressDetail}>
            {questionsAnswered} of {TOTAL_QUESTIONS} questions answered
          </span>
        </div>
        <div style={styles.progressBarTrack}>
          <div style={{ ...styles.progressBarFill, width: `${progressPct}%` }} />
        </div>
        <div style={styles.progressSteps}>
          {SECTIONS.map((s, i) => (
            <div
              key={s.key}
              style={{
                ...styles.progressDot,
                ...(i === currentStep ? styles.progressDotActive : {}),
                ...(i < currentStep ? styles.progressDotCompleted : {}),
              }}
              title={s.title}
              onClick={async () => {
                if (i !== currentStep) {
                  await saveProgress();
                  setCurrentStep(i);
                }
              }}
            >
              {i < currentStep ? '\u2713' : i + 1}
            </div>
          ))}
        </div>
      </div>

      {/* Current Step Questions */}
      <WizardStepView
        section={currentSection}
        stepIndex={currentStep}
        totalSteps={SECTIONS.length}
        responses={responses}
        onAnswer={handleAnswer}
      />

      {/* Navigation Buttons */}
      <div style={styles.navButtons}>
        <button
          className="button secondary"
          onClick={handlePrevious}
          disabled={currentStep === 0 || saving}
        >
          Previous
        </button>

        <div style={styles.navRight}>
          {!isLastStep && (
            <button
              className="button"
              onClick={handleNext}
              disabled={saving}
            >
              Next
            </button>
          )}
          {isLastStep && (
            <button
              className="button"
              onClick={handleComplete}
              disabled={completing || saving}
              style={styles.completeButton}
            >
              {completing ? 'Completing...' : 'Complete Assessment'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================
const styles = {
  page: {
    padding: 0,
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 8,
  },
  pageHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    margin: 0,
    color: '#111827',
  },
  backButton: {
    fontSize: 13,
    padding: '6px 14px',
  },
  savingIndicator: {
    fontSize: 12,
    color: '#6b7280',
    fontStyle: 'italic',
  },

  // ── Progress ──
  progressCard: {
    padding: 20,
    marginBottom: 20,
  },
  progressInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  progressLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: '#111827',
  },
  progressDetail: {
    fontSize: 12,
    color: '#6b7280',
  },
  progressBarTrack: {
    height: 8,
    backgroundColor: '#e5e7eb',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 16,
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#3b82f6',
    borderRadius: 4,
    transition: 'width 0.3s ease',
  },
  progressSteps: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 4,
  },
  progressDot: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 600,
    color: '#9ca3af',
    backgroundColor: '#f3f4f6',
    border: '2px solid #e5e7eb',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flexShrink: 0,
  },
  progressDotActive: {
    color: '#ffffff',
    backgroundColor: '#3b82f6',
    borderColor: '#3b82f6',
  },
  progressDotCompleted: {
    color: '#ffffff',
    backgroundColor: '#22c55e',
    borderColor: '#22c55e',
  },

  // ── Step Header ──
  stepHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 8,
  },
  stepTitle: {
    fontSize: 18,
    fontWeight: 700,
    margin: 0,
    color: '#111827',
  },
  stepCounter: {
    fontSize: 13,
    color: '#6b7280',
    fontWeight: 500,
  },

  // ── Questions ──
  questionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  questionCard: {
    padding: 16,
  },
  questionRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
  },
  questionIdBadge: {
    flexShrink: 0,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: 'monospace',
  },
  questionText: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 1.5,
  },
  radioGroup: {
    display: 'flex',
    gap: 10,
    marginLeft: 44,
  },
  radioLabel: {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  },
  radioInput: {
    position: 'absolute',
    opacity: 0,
    width: 0,
    height: 0,
    pointerEvents: 'none',
  },
  radioButton: {
    display: 'inline-block',
    padding: '6px 20px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    border: '1px solid #d1d5db',
    backgroundColor: '#f9fafb',
    color: '#6b7280',
    transition: 'all 0.15s ease',
    userSelect: 'none',
  },
  radioButtonYesActive: {
    backgroundColor: '#dcfce7',
    borderColor: '#22c55e',
    color: '#166534',
  },
  radioButtonNoActive: {
    backgroundColor: '#fee2e2',
    borderColor: '#ef4444',
    color: '#991b1b',
  },

  // ── Navigation Buttons ──
  navButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 24,
    paddingTop: 16,
    borderTop: '1px solid #e5e7eb',
  },
  navRight: {
    display: 'flex',
    gap: 8,
  },
  completeButton: {
    backgroundColor: '#22c55e',
    borderColor: '#22c55e',
  },

  // ── Loading / Error / Empty ──
  loadingMsg: {
    textAlign: 'center',
    padding: 40,
    color: '#6b7280',
    fontSize: 14,
  },
  errorMsg: {
    textAlign: 'center',
    padding: 40,
    color: '#dc2626',
    fontSize: 14,
  },
  emptyMsg: {
    textAlign: 'center',
    padding: 40,
    color: '#9ca3af',
    fontSize: 14,
  },

  // ── Results View ──
  resultsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  resultsTitle: {
    fontSize: 22,
    fontWeight: 700,
    margin: 0,
    color: '#111827',
  },
  scoreCard: {
    padding: 24,
    marginBottom: 20,
  },
  scoreCardInner: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    flexWrap: 'wrap',
  },
  scoreCardInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  sectionHeading: {
    fontSize: 16,
    fontWeight: 600,
    color: '#111827',
    margin: '0 0 12px',
  },
  sectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 12,
    marginBottom: 24,
  },
  sectionBreakdownCard: {
    padding: 16,
  },
  sectionBreakdownHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  sectionBreakdownName: {
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
  },

  // ── Score Bar ──
  scoreBarTrack: {
    height: 6,
    backgroundColor: '#e5e7eb',
    borderRadius: 3,
    overflow: 'hidden',
  },
  scoreBarFill: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.4s ease',
  },
};

export default RiskAssessmentWizard;
